"use client";

import { useMemo } from "react";
import { BedDouble, MapPin } from "lucide-react";
import { useLocale } from "@/lib/locale/locale-provider";
import type {
  AccommodationItem,
  AccommodationsSectionProps,
} from "@/lib/sections/accomodations.types";
import { AccommodationCard } from "./AccomodationCard";
import styles from "./accommodations.module.css";

function normalizeItems(
  items: AccommodationItem[] | undefined,
  recommended: AccommodationItem | undefined,
  others: AccommodationItem[],
) {
  const source = items?.length
    ? items
    : ([recommended, ...others].filter(Boolean) as AccommodationItem[]);

  const seen = new Set<string>();

  return source
    .filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }

      seen.add(item.id);
      return true;
    })
    .map((item) =>
      recommended?.id === item.id
        ? { ...item, recommended: true }
        : item,
    );
}

export function AccommodationsSection({
  title,
  subtitle,
  tags = [],
  items,
  recommended,
  others = [],
  notes = [],
}: AccommodationsSectionProps) {
  const locale = useLocale();
  const accommodations = useMemo(
    () => normalizeItems(items, recommended, others),
    [items, recommended, others],
  );
  const eyebrow =
    locale === "fr"
      ? "Se loger a proximite"
      : locale === "en"
        ? "Stay nearby"
        : "Unterkuenfte in der Naehe";
  const notesHeading =
    locale === "fr"
      ? "Bon a savoir"
      : locale === "en"
        ? "Good to know"
        : "Gut zu wissen";

  if (!accommodations.length && !notes.length) {
    return null;
  }

  return (
    <div className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <p className={styles.eyebrow}>
            <BedDouble size={16} aria-hidden="true" />
            <span>{eyebrow}</span>
          </p>

          <h2 className={styles.title}>{title}</h2>

          {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}

          {tags.length ? (
            <div className={styles.tags}>
              {tags.map((tag) => (
                <span key={tag} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {accommodations.length ? (
          <div className={styles.grid}>
            {accommodations.map((item, index) => (
              <AccommodationCard key={item.id} item={item} index={index} />
            ))}
          </div>
        ) : null}

        {notes.length ? (
          <div className={styles.notes}>
            <p className={styles.notesHeading}>
              <MapPin size={16} aria-hidden="true" />
              <span>{notesHeading}</span>
            </p>

            <div className={styles.notesList}>
              {notes.map((note, index) => (
                <p key={`${note}-${index}`} className={styles.note}>
                  {note}
                </p>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
